import { array } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismButton,
  PrismDrawer,
  PrismLoadingBar,
  PrismTabsContainer,
} from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import GeneralSmartInsightsDetailViewListArchivedInsights from 'lumen/components/general/smart-insights/detail-view/list-archived-insights';
import GeneralSmartInsightsDetailViewSmartInsightEntryList from 'lumen/components/general/smart-insights/detail-view/smart-insight-entry-list';
import GeneralSmartInsightsDetailViewSmartInsightKpiBlocks from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-blocks';
import type { SmartInsightDay } from 'lumen/models';
import {
  isSmartInsightsTab,
  SMART_INSIGHTS_KPI_COMPARISON_PERIODS,
  SMART_INSIGHTS_TABS,
  type SmartInsightsArchiveReason,
  type SmartInsightsKpiComparisonPeriod,
  type SmartInsightsTab,
} from 'lumen/utils/constants/smart-insights';

import styles from './drawer-smart-insights.module.css';

interface MarketSmartInsightsDrawerSmartInsightsSignature {
  Args: {
    collapseDrawer: () => void;
    isOpen: boolean;
    onArchive: (reason: SmartInsightsArchiveReason) => void;
    positionTop: number;
    setSmartInsightsTab: (tab: SmartInsightsTab) => void;
    smartInsightDay: SmartInsightDay | null;
    smartInsightsTab: SmartInsightsTab;
  };
  Element: HTMLButtonElement;
}

export default class MarketSmartInsightsDrawerSmartInsights extends Component<MarketSmartInsightsDrawerSmartInsightsSignature> {
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked kpiComparisonPeriod: SmartInsightsKpiComparisonPeriod =
    SMART_INSIGHTS_KPI_COMPARISON_PERIODS.YESTERDAY;

  SMART_INSIGHTS_TABS = SMART_INSIGHTS_TABS;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get drawerContentHeight() {
    return htmlSafe(`height: calc(100vh - ${this.args.positionTop}px);`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedStayDate() {
    const { stayDate } = this.args.smartInsightDay ?? {};
    return dayjs(stayDate).format('ddd, MMM D');
  }

  @action
  onSelectTab(tabId: string): void {
    if (!isSmartInsightsTab(tabId)) {
      return;
    }

    this.args.setSmartInsightsTab(tabId);

    this.usageAnalytics.track('Smart Insights Tab Selected', {
      eventTrigger: 'smart-insights-tabs',
      product: 'smart-insights-multi-property',
      tab: tabId,
      hotelinfoId: this.args.smartInsightDay?.hotelinfoId ?? null,
    });
  }

  @action setKpiComparisonPeriod(
    period: SmartInsightsKpiComparisonPeriod,
  ): void {
    this.kpiComparisonPeriod = period;
  }

  <template>
    <PrismDrawer
      @contentPadding="none"
      @isOpen={{@isOpen}}
      @positionTop={{@positionTop}}
      class="portfolio-smart-insights-drawer"
    >
      <:content>
        <div
          class="{{styles.content}} relative flex flex-col"
          style={{this.drawerContentHeight}}
        >
          <div class="{{styles.header}} flex flex-col gap-800 p-400">
            <div class="flex flex-row items-center justify-between gap-200">
              <h3 class="truncate">
                {{@smartInsightDay.hotelName}}
              </h3>

              <PrismButton
                @buttonType="ghost"
                @icon="cross"
                @onClick={{@collapseDrawer}}
              />
            </div>

            <div class="text-300--medium text-neutral-subdued">
              {{this.formattedStayDate}}
            </div>
          </div>

          {{#if @smartInsightDay}}
            <div class={{styles.body}}>
              {{! template-lint-disable no-duplicate-id }}
              <PrismTabsContainer
                @activeTabId={{@smartInsightsTab}}
                @onSelectTab={{this.onSelectTab}}
                @visibleTabIds={{array
                  this.SMART_INSIGHTS_TABS.ACTIVE
                  this.SMART_INSIGHTS_TABS.ARCHIVED
                }}
                as |TabsContainer|
              >
                <TabsContainer.header>
                  <:default as |Header|>
                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}
                      data-test-tab="active"
                    >
                      {{t "smart_insights.tabs.active"}}
                    </Header.tab>

                    <Header.tab
                      @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}
                      data-test-tab="archived"
                    >
                      <span class="flex items-center gap-100">
                        {{t "smart_insights.tabs.archived"}}
                        {{#if
                          (and
                            this.smartInsights.archivedInsightDays.length
                            (not
                              this.smartInsights.getArchivedInsightsTask.isRunning
                            )
                            (not this.smartInsights.hasArchivedInsightsError)
                          )
                        }}
                          <PrismBadge
                            @isSmall={{true}}
                            @label={{this.smartInsights.archivedInsightDays.length}}
                            @type="neutral"
                          />
                        {{/if}}
                      </span>
                    </Header.tab>
                  </:default>
                </TabsContainer.header>

                <TabsContainer.content as |Content|>
                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ACTIVE}}>
                    {{#if this.smartInsights.getArchivedInsightsTask.isRunning}}
                      <PrismLoadingBar
                        data-test-id="archived-insights-loading-active-tab"
                      />
                    {{/if}}

                    <div
                      class="smart-insight-detail-view flex flex-col gap-600 overflow-auto p-400"
                    >
                      <GeneralSmartInsightsDetailViewSmartInsightEntryList
                        @isArchiving={{this.smartInsights.archiveInsightTask.isRunning}}
                        @onArchive={{@onArchive}}
                        @openInNewTab={{true}}
                        @product="smart-insights-multi-property"
                        @smartInsightDay={{@smartInsightDay}}
                      />

                      {{! Source modules always open in a new tab here, so users don't lose the portfolio view. }}
                      <GeneralSmartInsightsDetailViewSmartInsightKpiBlocks
                        @openInNewTab={{true}}
                        @setSmartInsightsKpiComparisonPeriod={{this.setKpiComparisonPeriod}}
                        @smartInsightDay={{@smartInsightDay}}
                        @smartInsightsKpiComparisonPeriod={{this.kpiComparisonPeriod}}
                      />
                    </div>
                  </Content.tab>

                  <Content.tab @id={{this.SMART_INSIGHTS_TABS.ARCHIVED}}>
                    <GeneralSmartInsightsDetailViewListArchivedInsights
                      @archivedInsights={{this.smartInsights.archivedInsightDays}}
                      @hasError={{this.smartInsights.hasArchivedInsightsError}}
                      @isLoading={{this.smartInsights.getArchivedInsightsTask.isRunning}}
                    />
                  </Content.tab>
                </TabsContainer.content>
              </PrismTabsContainer>
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismDrawer>
  </template>
}
